import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  Renderer2,
  NgZone,
  inject,
  Injector,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { select } from '@ngxs/store';
import { debounceTime, skip, Subject, Subscription } from 'rxjs';
import { AnimationDurations } from 'src/app/core/angular-animations/angular-animations';
import { UiState } from 'src/app/core/state/ui.state';

export interface FitToViewportCalculateOptions {
  /** Only increase max-height; never unset/shrink (used during mobile table handoff). */
  growOnly?: boolean;
  /** Run even when `suspendRecalculation` is true. */
  force?: boolean;
  /** Upper bound for max-height (e.g. fullscreen table height during handoff). */
  maxHeightCap?: number;
}

/**
 * Directive that automatically sets an element's max-height to fit the available viewport space.
 * It calculates the distance from the element's top position to the bottom of the viewport,
 * optionally accounting for a bottom offset.
 *
 * The directive automatically recalculates on:
 * - Window resize
 * - Window scroll
 * - Toolbar collapse/expand state changes
 * - Element becoming visible (via IntersectionObserver)
 * - Initial component render (with retry mechanism)
 *
 * @example
 * ```html
 * <!-- Basic usage - fits to bottom of viewport -->
 * <div class="main-table-container" appFitToViewport></div>
 *
 * <!-- With bottom offset (e.g., for pagination controls) -->
 * <div class="main-table-container" appFitToViewport [bottomOffset]="80"></div>
 *
 * <!-- Conditionally disabled -->
 * <div class="main-table-container" appFitToViewport [fitToViewportDisabled]="someCondition"></div>
 * ```
 */
@Directive({
  selector: '[appFitToViewport]',
  standalone: true,
  exportAs: 'fitToViewportDirective',
})
export class FitToViewportDirective implements AfterViewInit, OnDestroy, OnChanges {
  private readonly injector = inject(Injector);

  @Input() bottomOffset = 0;
  @Input() minHeight = 50;
  @Input() debounceTime = 100;
  @Input() fitToViewportDisabled = false;
  /** Keeps the current max-height; skips resize/scroll recalculation while true. */
  @Input() suspendRecalculation = false;
  /** Always occupy remaining viewport space, even when table content is shorter. */
  @Input() fillAvailableViewport = false;

  private recalculateSubject?: Subject<void>;
  private subscriptions?: Subscription;
  private intersectionObserver?: IntersectionObserver;
  private hasCalculatedSuccessfully = false;
  private isInitialized = false;

  private readonly toolbarCollapsed = select(UiState.isToolbarCollapsed);

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    if (!this.fitToViewportDisabled) {
      this.initialize();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fitToViewportDisabled && !changes.fitToViewportDisabled.firstChange) {
      if (this.fitToViewportDisabled) {
        this.cleanup();
      } else {
        this.initialize();
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.fitToViewportDisabled || this.suspendRecalculation || !this.recalculateSubject) {
      return;
    }
    this.recalculateSubject.next();
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.fitToViewportDisabled || this.suspendRecalculation || !this.recalculateSubject) {
      return;
    }
    this.recalculateSubject.next();
  }

  private initialize(): void {
    if (this.isInitialized || this.fitToViewportDisabled) {
      return;
    }

    this.isInitialized = true;
    this.recalculateSubject = new Subject<void>();
    this.subscriptions = new Subscription();

    this.subscriptions.add(
      this.recalculateSubject.pipe(debounceTime(this.debounceTime)).subscribe(() => {
        this.calculateAndSetMaxHeight();
      })
    );

    // Defer initial calculation to ensure DOM is fully laid out
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() => {
          this.calculateAndSetMaxHeight();
          // If initial calculation failed, retry after a short delay
          if (!this.hasCalculatedSuccessfully && this.recalculateSubject) {
            setTimeout(() => this.recalculateSubject?.next(), 100);
          }
        });
      });
    });

    // Set up IntersectionObserver to detect when element becomes visible
    this.setupIntersectionObserver();

    this.subscriptions.add(
      toObservable(this.toolbarCollapsed, { injector: this.injector })
        .pipe(skip(1))
        .subscribe(() => {
          // Wait for animation to complete before recalculating
          setTimeout(() => {
            this.recalculateSubject?.next();
          }, AnimationDurations.TOOLBAR_SLIDE);
        })
    );
  }

  private cleanup(): void {
    if (!this.isInitialized) {
      return;
    }

    this.isInitialized = false;
    this.hasCalculatedSuccessfully = false;

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
      this.subscriptions = undefined;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }

    if (this.recalculateSubject) {
      this.recalculateSubject.complete();
      this.recalculateSubject = undefined;
    }
    this.removeMaxHeight();
  }

  calculateAndSetMaxHeight(options: FitToViewportCalculateOptions = {}): void {
    if (this.fitToViewportDisabled) {
      return;
    }
    if (this.suspendRecalculation && !options.force) {
      return;
    }

    const element = this.el.nativeElement as HTMLElement;
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const availableSpace = viewportHeight - rect.top - this.bottomOffset;

    // If element is completely off-screen below viewport or has invalid position, skip
    if (rect.top >= viewportHeight || rect.top < 0) {
      if (!options.growOnly) {
        this.removeMaxHeight();
      }
      return;
    }

    // If available space is too small, don't constrain it
    if (availableSpace <= this.minHeight) {
      if (!options.growOnly) {
        this.removeMaxHeight();
      }
      return;
    }

    // Mark that we've had a valid calculation
    this.hasCalculatedSuccessfully = true;

    let nextMaxHeight = Math.max(availableSpace, this.minHeight);
    if (options.maxHeightCap != null && Number.isFinite(options.maxHeightCap)) {
      nextMaxHeight = Math.min(nextMaxHeight, Math.max(options.maxHeightCap, this.minHeight));
    }

    if (options.growOnly) {
      const currentMaxHeight = Number.parseFloat(getComputedStyle(element).maxHeight);
      if (Number.isFinite(currentMaxHeight) && currentMaxHeight >= nextMaxHeight - 0.5) {
        return;
      }
      // Always keep an explicit px max-height during handoff — never unset.
      this.renderer.setStyle(element, 'max-height', `${nextMaxHeight}px`);
      return;
    }

    this.applyViewportHeight(element, nextMaxHeight, availableSpace);
  }

  private applyViewportHeight(
    element: HTMLElement,
    nextMaxHeight: number,
    availableSpace: number
  ): void {
    if (this.fillAvailableViewport) {
      this.renderer.setStyle(element, 'max-height', `${nextMaxHeight}px`);
      this.renderer.setStyle(element, 'min-height', `${nextMaxHeight}px`);
      return;
    }

    const elementHeight = element.scrollHeight;
    this.renderer.removeStyle(element, 'min-height');

    // Only set max-height if the element's natural height exceeds available space
    if (elementHeight > availableSpace) {
      this.renderer.setStyle(element, 'max-height', `${nextMaxHeight}px`);
      return;
    }

    this.renderer.setStyle(element, 'max-height', 'unset');
  }

  private setupIntersectionObserver(): void {
    if (this.fitToViewportDisabled) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.fitToViewportDisabled) {
              this.ngZone.run(() => {
                this.recalculateSubject?.next();
              });
            }
          });
        },
        { threshold: 0.01 }
      );

      this.intersectionObserver.observe(this.el.nativeElement);
    });
  }

  private removeMaxHeight(): void {
    this.renderer.removeStyle(this.el.nativeElement, 'max-height');
    this.renderer.removeStyle(this.el.nativeElement, 'min-height');
  }
}
